/**
 * Every decision `/ebay` makes, and every mutation it APPLIES.
 *
 * The same split as lib/sheetView.ts and lib/masterDataView.ts, for the same
 * reason (the STANDING RULE carried through the Task 7-11 reviews): pinning a
 * DECISION is not enough, because every escape those reviews found was planted
 * at a CALL SITE. So two layers live here, and views/Ebay.tsx computes nothing:
 *
 *  1. THE DECISIONS: which state the connection is in, what a granted eBay
 *     scope actually permits in German, whether a 409 is the repair path or a
 *     surprise. Pure, exhaustively driven from Node (test/ebayView.test.ts).
 *  2. THE ORCHESTRATION: `*Action`-shaped functions taking their collaborators
 *     as parameters, so the code that decides WHAT IS SENT -- above all the one
 *     request that turns eBay syncing on -- is callable with no renderer.
 *
 * ── WHY THIS VIEW IS THE LARGER HALF OF THE N3 MITIGATION ───────────────────
 * eBay has the largest blast radius of any connection this Admin owns (plan 3c
 * interface D13: orders including buyer PII, listing write, payout history) and
 * it is the ONE provider where no granted scope can name the account. The three
 * scopes the platform requests (api/src/ebay.js's EBAY_SCOPE) are
 * `sell.fulfillment.readonly`, `sell.inventory` and `sell.finances`; none of
 * them is `commerce.identity.readonly`, so "which eBay account did this
 * connection land in?" is a question this SPA genuinely cannot answer.
 * Everything below follows from that asymmetry:
 *
 *  - CONNECTING DOES NOT ENABLE THE FEATURE. `POST /admin-api/ebay/oauth-finish`
 *    stores the connection and deliberately does NOT set `feature.ebay`
 *    (api/src/adminRoutes.ts:1129-1189 -- it answers `{ connected: true }` and
 *    writes no setting), so the flag is this view's to turn on and a human
 *    confirmation is the natural gate. `finishEbayConnect` below is the whole
 *    finish path and it patches nothing.
 *  - THE GATE IS A TOTAL FUNCTION, NOT A SCREEN. `ebayViewModel` answers
 *    `'confirm'` for EVERY active connection whose flag is off -- reached the
 *    same way after a fresh landing and after a reload, because it is derived
 *    from `GET /admin-api/ebay` rather than from having just seen a redirect.
 *  - THE GATE IS ENFORCED IN THE ACTION, NOT ONLY ON THE BUTTON.
 *    `enableEbayAction` refuses an unconfirmed call itself, so a `disabled`
 *    attribute lost at the JSX call site still cannot turn eBay syncing on.
 *  - AN IDENTITY WE CANNOT READ IS NEVER RENDERED. There is no `ebayIdentity`
 *    in this file and there must not be one: the confirm panel states plainly
 *    that the account name is unreadable and offers the check the operator CAN
 *    make (Mein eBay -> Kontoeinstellungen -> Anwendungen), plus the granted
 *    scopes, the tenant, the connect time and a revocation link. Amendment E1
 *    proposes the scope that would fix this; until it lands, an implied
 *    identity would be worse than an admitted gap.
 */
import type { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';
import { t } from './i18n';
import { errorMessage } from '../components/ErrorNotice';
import { featuresErrorMessage, type MutationShell } from './masterDataView';
import { startProviderConnect } from './oauthLanding';
import { ebayStatusPill, formatDateTime, splitScopes } from './startView';
import { invalidateMutation, reportAuthFailure } from '../queries';
import type { PillKind } from '../components/StatusPill';
import type {
  EbayConnection, EbayConnectionStatus, EbayFinish, Features, FeaturesResponse, OAuthStart,
} from './types';

/** `GET /admin-api/ebay` (api/src/adminRoutes.ts:1190-1199) answers both
 *  halves in one response, which is why this view needs no second query for
 *  the flag it is about to change. */
export interface EbayStatusResponse {
  connection: EbayConnection | null;
  features: Features;
}

export type { MutationShell };

// ── error copy ───────────────────────────────────────────────────────────────

/**
 * The codes `/admin-api/ebay/*` actually answers. Literal `t()` calls so
 * test/i18nKeys.test.ts can prove each string exists; anything else falls
 * through to the shell's generic `error.<code>` resolver.
 *
 * `ebay_app_not_configured` is deliberately ABSENT: like sheetView.ts's
 * `template_not_configured` it is a NAMED OPERATOR STATE, not an error toast --
 * a platform secret that was never set (adminRoutes.ts:1121, :1135), which no
 * amount of retrying by this tenant can fix. `startEbayConnect` intercepts it
 * before this resolver is ever reached.
 *
 * `invalid_state` is absent for the Task 11 reason: it is the security screen
 * (lib/oauthLanding.ts's `finishFailureOutcome`), and arriving here with it
 * would mean the landing flow had already gone wrong.
 */
export function ebayErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'not_connected': return t('error.not_connected');
      case 'exchange_failed': return t('error.exchange_failed');
    }
  }
  return errorMessage(error);
}

// ── what the three scopes actually permit ────────────────────────────────────

/**
 * eBay states its scopes as URIs under its own API host. Parsed rather than
 * prefix-matched so a lookalike string cannot borrow a friendly German label:
 * the host must be eBay's and the path must be the scope path, or the value is
 * not an eBay scope at all and is shown verbatim.
 *
 * A BARE SHORT NAME IS ACCEPTED AS ITSELF. `connections.scopes` is whatever the
 * provider's token response carried (adminRoutes.ts stores `tok.scope`
 * untouched), and the same grant appears in the wild both fully qualified and
 * as `sell.inventory`. They name one grant, so they get one label.
 *
 * The host literals are written without a scheme on purpose: eslint.config.js's
 * NO_API_HOSTNAME bans `https://…api.` literals so that no base-URL chain can
 * be rebuilt in this SPA, and an OAuth scope identifier is not a URL this
 * client ever fetches.
 */
const SCOPE_HOSTS = new Set(['api.ebay.com', 'api.sandbox.ebay.com']);
const SCOPE_PATH = '/oauth/api_scope/';

export function ebayScopeName(scope: string): string | null {
  if (!scope.includes('://')) return scope;
  let url: URL;
  try {
    url = new URL(scope);
  } catch {
    return null;
  }
  if (!SCOPE_HOSTS.has(url.hostname)) return null;
  if (!url.pathname.startsWith(SCOPE_PATH)) return null;
  return url.pathname.slice(SCOPE_PATH.length);
}

/**
 * WHAT IT GRANTS, not what it is called. An owner who does not want StackStock
 * reading payout data has to be able to learn that from this line, before
 * consenting -- "sell.finances" does not say it and "Gebühren- und
 * Auszahlungsdaten lesen" does.
 *
 * Anything outside the three is rendered VERBATIM (sheetView.ts's `scopeLabel`,
 * same discipline): inventing a friendly name for a scope nobody asked for
 * would describe a grant nobody checked.
 */
export function ebayScopeLabel(scope: string): string {
  switch (ebayScopeName(scope)) {
    case 'sell.fulfillment.readonly': return t('ebay.scope.fulfillment');
    case 'sell.inventory': return t('ebay.scope.inventory');
    case 'sell.finances': return t('ebay.scope.finances');
    default: return scope;
  }
}

/**
 * The three the platform's authorize URL asks for, transcribed from
 * api/src/ebay.js's `EBAY_SCOPE` (its own comment: authorize and refresh must
 * use the SAME scope set, so this list is the whole grant and never a subset).
 *
 * Rendered on the consent panel BEFORE the redirect, through the same
 * `ebayScopeLabel` the confirm panel uses afterwards -- one table, so the
 * promise and the receipt cannot drift.
 */
export const REQUESTED_SCOPES = [
  'sell.fulfillment.readonly',
  'sell.inventory',
  'sell.finances',
] as const;

export function requestedScopeLabels(): string[] {
  return REQUESTED_SCOPES.map(ebayScopeLabel);
}

// ── the state of the connection, and THE GATE ────────────────────────────────

export interface EbayConnectionDetail {
  pill: { kind: PillKind; label: string };
  connectedAtLine: string;
  /** Already German (or verbatim); the view maps nothing of its own. */
  scopes: string[];
}

/**
 * FOUR STATES OUT OF ONE RESPONSE, and the order of the tests is the argument:
 *
 *  1. no row at all -> eBay was never connected.
 *  2. `status !== 'active'` -> disconnected. Checked as the ABSENCE of the one
 *     good value, never as the presence of a bad one: the server never deletes
 *     the row on disconnect, it flips `status` to `'revoked'`
 *     (adminRoutes.ts:1245), `'error'` is declared but unwritten, and api.ts's
 *     unchecked `body as T` cast means an unrecognised string is reachable.
 *     Fail closed, exactly as capabilities.ts's EBAY_HEALTHY_STATUSES does.
 *  3. active + `feature.ebay` OFF -> THE CONFIRMATION GATE. This is the whole
 *     N3 mitigation and it is a TOTAL branch, not a screen somebody remembers
 *     to route to: there is no path from "connected" to "syncing" that does not
 *     pass through it, and a reload lands back here rather than skipping it.
 *  4. active + flag on -> the ordinary connected panel.
 *
 * `features.ebay === true`, never `features.ebay` alone: the same unchecked
 * cast that lets `status` be an unknown string lets the flag be the STRING
 * `'false'`, which is truthy. The server's own `featuresOf` compares
 * `f.ebay === true` (api/src/adminContext.ts:250) for this reason; a truthy
 * test here would fail OPEN, skipping the gate.
 */
export type EbayViewModel =
  | { kind: 'never' }
  | { kind: 'disconnected'; pill: { kind: PillKind; label: string }; note: string }
  | { kind: 'confirm'; detail: EbayConnectionDetail }
  | { kind: 'active'; detail: EbayConnectionDetail };

/**
 * WHICH CONNECTION AN ATTESTATION BELONGS TO (Task 12 review, MIN-2).
 *
 * "Ich habe geprüft, dass dies mein eBay-Konto ist" is a statement about ONE
 * grant. The checkbox that carries it is component state, and component state
 * outlives a re-render -- so the view resets it whenever this value changes.
 *
 * BOTH FIELDS, not `connectedAt` alone: the server never deletes the row on
 * disconnect, it flips `status` and leaves `connected_at` exactly as it was
 * (adminRoutes.ts:1245), so a status-blind key would carry an attestation
 * across a disconnect. A reconnect writes a fresh `connected_at`, so the pair
 * changes for both transitions.
 *
 * Not exploitable today -- every connect is a top-level navigation, so a new
 * grant always arrives on a fresh mount with the box unticked. It is a latent
 * hazard pointed straight at the property this view defends: the day anything
 * reconnects without a full page load, a stale `true` would carry the PREVIOUS
 * account's attestation onto a NEW grant.
 */
export function connectionIdentity(status: EbayStatusResponse): string | null {
  const { connection } = status;
  if (connection === null) return null;
  return `${connection.status} ${connection.connectedAt}`;
}

export function ebayViewModel(status: EbayStatusResponse): EbayViewModel {
  const { connection, features } = status;
  if (connection === null) return { kind: 'never' };
  if (connection.status !== 'active') {
    // 'error' still reads as an error (something is broken); everything else --
    // 'revoked' and any value outside the union -- reads as disconnected. Two
    // pills, one reconnect route (lib/startView.ts's `ebayCardModel`, same
    // split).
    const shown: EbayConnectionStatus = connection.status === 'error' ? 'error' : 'revoked';
    return { kind: 'disconnected', pill: ebayStatusPill(shown), note: t('ebay.disconnected.note') };
  }
  const on = features.ebay === true;
  const detail: EbayConnectionDetail = {
    pill: on ? ebayStatusPill('active') : { kind: 'warn', label: t('ebay.pill.pending') },
    connectedAtLine: t('ebay.connectedAt', { date: formatDateTime(connection.connectedAt) }),
    scopes: splitScopes(connection.scopes).map(ebayScopeLabel),
  };
  return on ? { kind: 'active', detail } : { kind: 'confirm', detail };
}

// ── shared mutation shell ────────────────────────────────────────────────────

/** Busy on, previous error cleared, run, invalidate THROUGH THE SHARED MAP
 *  (queries.ts's `INVALIDATES`), report. Busy always ends false -- a panel left
 *  permanently disabled after one failed write is indistinguishable from a hung
 *  app (lib/masterDataView.ts's own `runMasterDataMutation`, same contract). */
export async function runEbayMutation<R>(
  mutation: string,
  run: () => Promise<R>,
  shell: MutationShell,
  options: { onSuccess?: (result: R) => void; resolveError?: (error: unknown) => string } = {},
): Promise<boolean> {
  shell.setBusy(true);
  shell.setError(null);
  try {
    const result = await run();
    invalidateMutation(shell.queryClient, mutation);
    options.onSuccess?.(result);
    return true;
  } catch (error) {
    // A 401 drives the SESSION, not just this panel's error line (I-1).
    reportAuthFailure(shell.queryClient, error);
    shell.setError((options.resolveError ?? ebayErrorMessage)(error));
    return false;
  } finally {
    shell.setBusy(false);
  }
}

// ── the departure ────────────────────────────────────────────────────────────

export interface StartEbayIO {
  start: () => Promise<OAuthStart>;
  setBusy: (busy: boolean) => void;
  setError: (message: string | null) => void;
  /** `503 ebay_app_not_configured` is a NAMED STATE, not an error toast. */
  setNotConfigured: (notConfigured: boolean) => void;
  /** Passed straight through to `startProviderConnect`, for its 401 report
   *  (final review I-1). */
  queryClient: QueryClient;
}

/**
 * "Mit eBay verbinden", and it is a TOP-LEVEL NAVIGATION -- delegated to
 * lib/oauthLanding.ts's `startProviderConnect` rather than reimplemented, so
 * both providers leave this SPA through the one function whose whole argument
 * (amendment A1's `__Host-oauth_bind` binding) lives in lib/navigation.ts. A
 * second way out of this page is the bug this delegation prevents.
 *
 * The only thing added on top is the 503: `EBAY_PLATFORM_CLIENT_ID` /
 * `_SECRET` / `_RUNAME` missing means the PLATFORM's eBay app was never
 * provisioned for this environment (adminRoutes.ts:1121). That is an operator
 * problem with a runbook, not something the tenant did, and rendering "Der
 * Vorgang ist fehlgeschlagen." over it sends the wrong person looking.
 */
export async function startEbayConnect(io: StartEbayIO): Promise<boolean> {
  io.setNotConfigured(false);
  let notConfigured = false;
  const started = await startProviderConnect({
    start: async () => {
      try {
        return await io.start();
      } catch (error) {
        notConfigured = error instanceof ApiError && error.code === 'ebay_app_not_configured';
        throw error;
      }
    },
    setBusy: io.setBusy,
    queryClient: io.queryClient,
    // The named state replaces the message rather than sitting beside it: two
    // renderings of one failure is how an operator learns to read neither.
    setError: (message) => io.setError(notConfigured ? null : message),
  });
  io.setNotConfigured(notConfigured);
  return started;
}

// ── oauth-finish ─────────────────────────────────────────────────────────────

export interface FinishEbayIO {
  queryClient: QueryClient;
  ebayFinish: (state: string) => Promise<EbayFinish>;
}

/**
 * The `onFinish` <ConnectOutcome> is given, and THE ABSENCE HERE IS THE POINT.
 *
 * It POSTs the parked jti and invalidates through the shared map. It does NOT
 * call `patchFeatures`, and no code path from a landing to a mutation exists
 * that does: an operator lured into a connect must still have to tick a box and
 * press a button before eBay starts reading their orders and writing their
 * listings. A `{ ebay: true }` added anywhere on this path deletes the entire
 * N3 mitigation while every screen still looks right.
 *
 * It deliberately does NOT catch: `runLandingFlow` classifies the failure, and
 * `400 invalid_state` must reach `finishFailureOutcome` as an ApiError to
 * become the security screen instead of a generic message.
 */
export async function finishEbayConnect(state: string, io: FinishEbayIO): Promise<EbayFinish> {
  const result = await io.ebayFinish(state);
  invalidateMutation(io.queryClient, 'ebayFinish');
  return result;
}

// ── the gate ─────────────────────────────────────────────────────────────────

export interface EnableEbayIO extends MutationShell {
  patchFeatures: (json: { ebay?: boolean; priceScan?: boolean }) => Promise<FeaturesResponse>;
}

/**
 * "eBay-Synchronisierung aktivieren" -- `PATCH /admin-api/features { ebay: true }`
 * (D10), and the ONE place in this SPA's eBay path where that request is built.
 *
 * `confirmed` IS CHECKED HERE, not only reflected in a `disabled` attribute.
 * A disabled button is a rendering; this is the gate. The distinction is the
 * difference between "the checkbox was lost in a refactor and nobody noticed"
 * and "the flag cannot be turned on without it" -- and the controller's own
 * mutation list names a bypassed gate as an attack to defend against, so the
 * defence must not live in JSX where a call-site mutation can lift it.
 *
 * `{ ebay: true }` is a LITERAL, never `!features.ebay` read back off state
 * that could be stale: this control exists only in the `confirm` panel, whose
 * precondition is already "the flag is off".
 */
export async function enableEbayAction(confirmed: boolean, io: EnableEbayIO): Promise<boolean> {
  if (!confirmed) {
    io.setError(t('ebay.confirm.required'));
    return false;
  }
  return runEbayMutation(
    'patchFeatures',
    () => io.patchFeatures({ ebay: true }),
    io,
    { resolveError: featuresErrorMessage },
  );
}

// ── disconnect, and the repair path ──────────────────────────────────────────

/**
 * Is this tenant disconnected in every OBSERVABLE way? The server's own
 * question, asked of the state it just answered 409 over (adminRoutes.ts:1204
 * and :1234): a 409 `not_connected` means either no row at all, or a revoked
 * row whose `feature.ebay` is already off -- both of which are "already done".
 *
 * Fail closed: a re-read that still shows an active connection, or the flag
 * still on, is NOT "bereits getrennt". Reporting success over a half-open
 * state would leave the owner believing eBay is disconnected while a job can
 * still run.
 */
export function alreadyDisconnected(status: EbayStatusResponse): boolean {
  if (status.features.ebay === true) return false;
  return status.connection === null || status.connection.status !== 'active';
}

export interface DisconnectEbayIO extends MutationShell {
  /** window.confirm at the call site; a function here so the confirmation is
   *  part of the action rather than something a call site may forget. */
  confirm: (message: string) => boolean;
  disconnect: () => Promise<EbayFinish>;
  /** `GET /admin-api/ebay`, re-read to decide whether a 409 is the repair
   *  path or a genuine surprise. */
  readEbay: () => Promise<EbayStatusResponse>;
  setNotice: (notice: string | null) => void;
}

/**
 * "eBay-Verbindung trennen".
 *
 * The confirmation names what the merged handler ACTUALLY does
 * (adminRoutes.ts:1201-1277), all four of them: the connection row is revoked,
 * the cached access token is dropped NOW rather than in ninety minutes,
 * `feature.ebay` is turned off in the same action -- and the encrypted refresh
 * token is KEPT, so an accidental disconnect can be undone by support without a
 * new consent round. That last one is stated out loud on purpose: letting an
 * owner believe the credential was destroyed makes the support recovery path
 * look like a security hole the first time it is used.
 *
 * A DECLINED CONFIRMATION SENDS NOTHING. No busy state, no request, no message.
 */
export async function disconnectEbayAction(io: DisconnectEbayIO): Promise<boolean> {
  if (!io.confirm(t('ebay.disconnect.confirm'))) return false;
  io.setNotice(null);
  io.setBusy(true);
  io.setError(null);
  try {
    await io.disconnect();
    invalidateMutation(io.queryClient, 'ebayDisconnect');
    io.setNotice(t('ebay.disconnect.done'));
    return true;
  } catch (error) {
    if (error instanceof ApiError && error.code === 'not_connected') {
      return await reportRepair(error, io);
    }
    io.setError(ebayErrorMessage(error));
    return false;
  } finally {
    io.setBusy(false);
  }
}

/**
 * The repair reading of a 409, and it is EARNED rather than assumed: the view
 * re-reads `GET /admin-api/ebay` and only says "bereits getrennt" when that
 * answer agrees. A re-read that fails, or that disagrees, falls back to the
 * ordinary error -- an optimistic "already disconnected" over a connection that
 * is still live is the one wrong answer this path can give.
 */
async function reportRepair(error: ApiError, io: DisconnectEbayIO): Promise<boolean> {
  let status: EbayStatusResponse;
  try {
    status = await io.readEbay();
  } catch {
    io.setError(ebayErrorMessage(error));
    return false;
  }
  if (!alreadyDisconnected(status)) {
    io.setError(ebayErrorMessage(error));
    return false;
  }
  invalidateMutation(io.queryClient, 'ebayDisconnect');
  io.setNotice(t('ebay.disconnect.alreadyDone'));
  return true;
}
